import { getApiErrorMessage } from "network/api-error-message";

// Client-side limits for money forms. MAX_AMOUNT fits every amount column (the smallest is
// decimal(12,2)); align it with the backend limit once CONTRACT.md X-05 is final.
export const MAX_AMOUNT = 999999999;
export const DESCRIPTION_MAX_LENGTH = 255;

/**
 * Message id for what's wrong with an amount typed in a money form, or null when it is
 * valid or still empty (an empty field keeps the submit button disabled instead).
 * `allowZero` accepts 0 (price list templates); negatives are then rejected separately.
 */
export const amountErrorId = (value, { allowZero = false } = {}) => {
  const text = value == null ? "" : String(value).trim();
  if (text === "") return null;
  const amount = Number(text);
  if (!Number.isFinite(amount)) return "gestion.money.amountInvalid";
  if (allowZero ? amount < 0 : amount <= 0) return allowZero ? "gestion.money.amountNegative" : "gestion.money.amountPositive";
  if (amount > MAX_AMOUNT) return "gestion.money.amountTooLarge";
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return "gestion.money.amountDecimals";
  return null;
};

export const isValidAmount = (value, options) =>
  String(value ?? "").trim() !== "" && amountErrorId(value, options) === null;

export const descriptionErrorId = (value) =>
  String(value ?? "").length > DESCRIPTION_MAX_LENGTH ? "gestion.money.descriptionTooLong" : null;

const messageValues = (intl) => ({
  max: intl.formatNumber(MAX_AMOUNT),
  maxLength: DESCRIPTION_MAX_LENGTH,
});

export const moneyFieldError = (intl, errorId) => (errorId ? intl.formatMessage({ id: errorId }, messageValues(intl)) : null);

/** Helper text for a description field: the error if any, otherwise a "n/255" counter. */
export const descriptionHelperText = (intl, value) =>
  moneyFieldError(intl, descriptionErrorId(value)) ??
  intl.formatMessage(
    { id: "gestion.money.descriptionCounter" },
    { count: String(value ?? "").length, maxLength: DESCRIPTION_MAX_LENGTH },
  );

const FIELD_MESSAGE_IDS = {
  amount: "gestion.money.serverAmount",
  price: "gestion.money.serverAmount",
  description: "gestion.money.serverDescription",
  name: "gestion.money.serverName",
};

/**
 * Spanish message for a failed money request. Laravel validation errors name raw fields
 * ("El campo amount…"), so known fields get our own wording instead.
 */
export const moneyRequestErrorMessage = (error, intl) => {
  const fieldErrors = error?.response?.data?.errors;
  if (error?.response?.status === 422 && fieldErrors && typeof fieldErrors === "object") {
    const ids = [...new Set(Object.keys(fieldErrors).map((field) => FIELD_MESSAGE_IDS[field]).filter(Boolean))];
    if (ids.length) return ids.map((id) => intl.formatMessage({ id }, messageValues(intl))).join(" ");
    return intl.formatMessage({ id: "gestion.money.serverInvalid" });
  }
  return getApiErrorMessage(error, intl.formatMessage({ id: "gestion.error.generic" }));
};

/**
 * How much of a payment goes beyond what the account owes. `available` is the account
 * balance (negative = debt). Per D4 the excess is kept as credit, so this only informs.
 */
export const overpaymentAmount = (amount, available) => {
  const paid = Number(amount);
  const balance = Number(available);
  if (!Number.isFinite(paid) || paid <= 0 || available == null || !Number.isFinite(balance)) return 0;
  const owed = Math.max(0, -balance);
  return Math.max(0, Math.round((paid - owed) * 100) / 100);
};

export const VOID_REASON_MIN_LENGTH = 10;
const MANUAL_METHODS = new Set(["cash", "transfer", "pos"]);

/**
 * A manual "Agregar saldo" credit: a successful manual payment not tied to any charge. The
 * backend has the final word (credit_not_voidable / credit_already_used).
 */
export const isVoidableCredit = (payment) =>
  String(payment?.status || "").toLowerCase() === "success" &&
  MANUAL_METHODS.has(String(payment?.method || "").toLowerCase()) &&
  (payment?.invoices || []).length === 0;

const VOID_CREDIT_ERROR_IDS = {
  credit_not_voidable: "gestion.paymentDetail.voidCredit.notVoidable",
  credit_already_used: "gestion.paymentDetail.voidCredit.alreadyUsed",
};

export const voidCreditErrorMessage = (error, intl) => {
  const id = VOID_CREDIT_ERROR_IDS[error?.response?.data?.code];
  return id ? intl.formatMessage({ id }) : moneyRequestErrorMessage(error, intl);
};
